import { WLContext } from '@contexts';
import { Button, Heading } from '@design-system';
import React, { useCallback, useState } from 'react';
import Layout from 'layouts/Admin';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useSession } from 'store/session';
import { Input } from '@design-system/inputs/BaseInputs';
import { useFetchTeamFolders } from 'hooks/useFetchTeamFolders';
import { Listing } from './components/Listing';
import { ITeamFolderPayload } from '@cincel/client';
import { useToggle } from 'hooks/useToggle';
import { Form } from './components/Form';
import { useDelete, usePatch, usePost } from './hooks';
import { cx } from '@emotion/css';

const DEFAULT_VALUES = {
  name: '',
};

export const DirectoriesPage: React.FC = (): JSX.Element | null => {
  const { color } = useContext(WLContext);

  const { team, accountLimits } = useSession();

  const { t } = useTranslation();

  const {
    data,
    isLoading,
    page,
    pageSize,
    setLike,
    setPage,
    setPageSize,
    refetch,
  } = useFetchTeamFolders();

  const [registry, setRegistry] = useState<ITeamFolderPayload>(DEFAULT_VALUES);

  const [action, setAction] = useState<'patch' | 'post' | null>(null);

  const [isOpen, toggle] = useToggle(false);

  const { mutateAsync: onDelete } = useDelete();

  const { mutateAsync: onPatch } = usePatch();

  const { mutateAsync: onPost } = usePost();

  const handleOnDelete = useCallback(async (v: string) => {
    await onDelete(v);
    await refetch();
  }, []);

  const handleOnPatch = useCallback(async (v: ITeamFolderPayload) => {
    await onPatch(v);
    await refetch();
    toggle(false);
  }, []);

  const handleOnPost = useCallback(async (v: ITeamFolderPayload) => {
    await onPost(v);
    await refetch();
    toggle(false);
  }, []);

  const preparePatch = useCallback((r: ITeamFolderPayload) => {
    setAction('patch');
    setRegistry(r);
    toggle(true);
  }, []);

  const preparePost = useCallback(() => {
    setAction('post');
    setRegistry(DEFAULT_VALUES);
    toggle(true);
  }, []);

  if (isLoading) return null;
  const isAdmin = String(team.role ?? '').toLowerCase() === 'admin';

  return (
    <Layout className="px-4 md:px-0">
      <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className={cx(isAdmin ? 'flex-[2]' : 'flex-1')}>
          <Heading className="!mb-2">
            {t(translations.FOLDERS_PAGES.MY_FOLDERS)}
          </Heading>
          <p className="text-sm font-medium text-gray-300">
            <Trans
              i18nKey={translations.FOLDERS_PAGES.SUB_HEADING}
              values={{ team: team?.name }}
            >
              <strong />
            </Trans>
          </p>
        </div>
        {isAdmin && (
          <div className="flex w-full flex-1 gap-4 lg:justify-end">
            <Button
              className="w-full px-8 text-white lg:w-auto"
              color={color}
              onClick={preparePost}
              size="sm"
              variant="solid"
            >
              {t(translations.FOLDERS_PAGES.NEW_FOLDER)}
            </Button>
          </div>
        )}
      </div>

      <Input
        className="mb-6 lg:w-[320px]"
        debounceInterval={500}
        onChange={setLike}
        placeholder={t(translations.COMMON.SERACH_BY_NAME)}
        primaryColor={color}
        size="sm"
      />

      <Listing
        data={data?.data}
        onDelete={handleOnDelete}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onUpdate={preparePatch}
        page={page}
        pageSize={pageSize}
        primaryColor={color}
        total={data?.count}
      />

      <Form
        action={action}
        defaultValues={registry}
        isOpen={isOpen}
        onSubmit={action === 'patch' ? handleOnPatch : handleOnPost}
        onToggle={toggle}
      />
    </Layout>
  );
};

export default DirectoriesPage;
